import { buildReceivingSummary } from "./receivingViewModel";
import type {
  CheckinItemReturn,
  CheckinItemReceivingStatus,
  CheckinItemStatus,
  EnrichedCheckinItem,
  ItemReturnStatus,
} from "./types";

export const ITEM_STATUS_COLORS: Record<CheckinItemStatus, string> = {
  PENDING: "var(--login-muted)",
  OK: "#16a34a",
  NOT_ARRIVED: "#ef4444",
  INCONGRUENT: "#d97706",
  UNAVAILABLE: "#9ca3af",
  DECISION: "#9333ea",
  SUBSTITUIDO: "#0f766e",
  DEVOLVIDO: "#6b7280",
};

export const ITEM_STATUS_LABELS: Record<CheckinItemStatus, string> = {
  PENDING: "Pendente",
  OK: "Ok",
  NOT_ARRIVED: "Não chegou",
  INCONGRUENT: "Divergente",
  UNAVAILABLE: "Indisponível",
  DECISION: "Decidindo",
  SUBSTITUIDO: "Substituído",
  DEVOLVIDO: "Devolvido",
};

export const RECEIVING_STATUS_LABELS: Record<
  CheckinItemReceivingStatus,
  string
> = {
  PENDING: "Pendente",
  PARTIAL: "Parcial",
  COMPLETE: "Completo",
  SURPLUS: "Sobra",
  SHORTFALL_CLOSED: "Encerrado",
};

export const RECEIVING_STATUS_COLORS: Record<
  CheckinItemReceivingStatus,
  string
> = {
  PENDING: "var(--login-muted)",
  PARTIAL: "#d97706",
  COMPLETE: "#16a34a",
  SURPLUS: "var(--login-accent)",
  SHORTFALL_CLOSED: "var(--login-error)",
};

const RETURN_STATUS_LABELS: Record<ItemReturnStatus, string> = {
  NONE: "",
  DECISION: "Pendente",
  SUBSTITUIDO: "Substituído",
  DEVOLVIDO: "Devolvido",
};

function buildReturnSummary(item: EnrichedCheckinItem) {
  const lot = selectLatestReturnLot(item.return_lots);
  if (!lot) return null;

  return {
    id: lot.id,
    quantity: lot.quantity,
    supplierName: lot.supplier_name,
    requestedAt: lot.requested_at,
    note: lot.request_note ?? lot.decision_note ?? null,
    status: lot.status,
    statusLabel: RETURN_STATUS_LABELS[lot.status],
  };
}

function getReturnLotSortTime(lot: CheckinItemReturn) {
  const time = Date.parse(lot.requested_at);
  return Number.isFinite(time) ? time : 0;
}

function selectLatestReturnLot(lots: CheckinItemReturn[] | undefined) {
  if (!lots?.length) return null;

  return lots.reduce((latest, current) => {
    const timeDiff =
      getReturnLotSortTime(current) - getReturnLotSortTime(latest);
    if (timeDiff > 0) return current;
    if (timeDiff < 0) return latest;
    return current.id.localeCompare(latest.id) > 0 ? current : latest;
  });
}

export function selectLatestActiveReturnLot(
  lots: CheckinItemReturn[] | undefined,
) {
  return selectLatestReturnLot(
    lots?.filter((lot) => lot.status === "DECISION"),
  );
}

interface BuildCheckinItemDisplayOptions {
  currentUserRole?: string;
  canManageCheckins?: boolean;
  isClientView: boolean;
  readOnly: boolean;
}

export function buildCheckinItemDisplay(
  item: EnrichedCheckinItem,
  {
    currentUserRole,
    canManageCheckins = currentUserRole === "super",
    isClientView,
    readOnly,
  }: BuildCheckinItemDisplayOptions,
) {
  const receivingSummary = buildReceivingSummary(item);
  const originalExpectedQuantity =
    item.original_expected_quantity ?? item.expected_quantity ?? 0;
  const returnSummary = buildReturnSummary(item);
  const isUnavailable = item.status === "UNAVAILABLE";
  const isDecision = item.status === "DECISION";
  const isFullDecision = isDecision && receivingSummary.pendingQuantity <= 0;
  const isReturned = item.status === "DEVOLVIDO";
  const isSubstituted = item.status === "SUBSTITUIDO";
  const hasSubstitutionHistory = item.return_status === "SUBSTITUIDO";
  const hasCompletedReturnDecision =
    item.return_status === "SUBSTITUIDO" || item.return_status === "DEVOLVIDO";
  const isTerminalOriginal =
    (isReturned || isSubstituted || hasCompletedReturnDecision) &&
    !(
      (isSubstituted || item.return_status === "SUBSTITUIDO") &&
      receivingSummary.pendingQuantity > 0
    );
  const showSubstitutionTag = hasSubstitutionHistory && !isSubstituted;
  const statusColor = isClientView
    ? "var(--login-muted)"
    : RECEIVING_STATUS_COLORS[receivingSummary.status];
  const canAct =
    canManageCheckins &&
    !isClientView &&
    !readOnly &&
    !isUnavailable &&
    !isFullDecision &&
    !isReturned &&
    !isTerminalOriginal;
  const canCancelReturn = canManageCheckins && !readOnly && isDecision;
  const canReplaceReturn = currentUserRole === "super" && canCancelReturn;
  const canResolveReturn = canCancelReturn;
  const canCorrectReturnDecision =
    currentUserRole === "super" && hasCompletedReturnDecision;
  const canReceive = canAct && !receivingSummary.isShortfallClosed;
  const canCloseShortfall =
    canAct &&
    receivingSummary.pendingQuantity > 0 &&
    !receivingSummary.isShortfallClosed;
  const cardOpacity =
    isUnavailable || isReturned || isTerminalOriginal ? 0.45 : 1;

  return {
    ...receivingSummary,
    receivingStatus: receivingSummary.status,
    expectedQuantity: receivingSummary.expectedQuantity,
    originalExpectedQuantity,
    returnedQuantity: item.returned_quantity ?? 0,
    returnSummary,
    statusLabel: RECEIVING_STATUS_LABELS[receivingSummary.status],
    statusColor,
    isUnavailable,
    isDecision,
    isReturned,
    isSubstituted,
    hasSubstitutionHistory,
    showSubstitutionTag,
    canAct,
    canResolveReturn,
    canReplaceReturn,
    canCancelReturn,
    canCorrectReturnDecision,
    canReceive,
    canCloseShortfall,
    cardOpacity,
  };
}
